import React, { useState } from "react"
import { Typography, Grid, Box, Popover, useTheme, IconButton } from "@mui/material"
import { Close, InfoOutlined } from "@mui/icons-material"
import { Formik } from "formik"
import { useDispatch } from "react-redux"
import AppTextField from "components/StyledComponents/AppTextField"
import AppSelect from "components/StyledComponents/AppSelect"
import AppButton from "components/StyledComponents/AppButton"
import CommonModal from "components/CommonModal"
import { createSectionTypeAction, updateSectionTypeAction } from "redux/superAdmin/actions/user"
import { validationSchemaSection } from "helpers/validationSchema"
import { sectionTypeGuide, sectionTypes } from "helpers/constant"

const SectionModal = ({ open, onClose, isEdit, sectionData }) => {
  const dispatch = useDispatch()
  const formikRef = React.useRef()
  const theme = useTheme()
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "15px",
      right: "15px",
      zIndex: 10,
      backgroundColor: theme.palette.background.main
    }
  }
  React.useEffect(() => {
    if (open && formikRef.current) {
      formikRef.current.resetForm()
    }
  }, [open])

  const initialValues = {
    name: isEdit ? sectionData?.name : "",
    type: isEdit ? sectionData?.section_key : "",
    is_active: isEdit ? sectionData?.is_active : true
  }

  const handleSubmit = (values) => {
    const payload = { ...values }
    const callback = () => {
      onClose()
    }

    if (isEdit) {
      dispatch(
        updateSectionTypeAction({
          id: sectionData.id,
          payload,
          callback
        })
      )
    } else {
      dispatch(
        createSectionTypeAction({
          payload,
          callback
        })
      )
    }
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchemaSection}
      onSubmit={handleSubmit}
      enableReinitialize={true}>
      {(formik) => (
        <CommonModal open={open} onClose={onClose} maxWidth="sm" hideAction={true}>
          <Typography variant="h2" className="mb-4 text-center" color="white">
            {isEdit ? "Edit Template" : "Add New Template"}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AppTextField
                name="name"
                label="Template Name"
                placeholder="e.g. Daily Briefing"
                value={formik.values.name}
                onChange={formik.handleChange}
                isRequired={true}
                isUpperCase={true}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography variant="body2" color="white">
                  Section Type
                </Typography>
                <InfoOutlined
                  onClick={(e) => setPopoverAnchor(e.currentTarget)}
                  sx={{
                    fontSize: 16,
                    color: "rgba(255,255,255,0.5)",
                    cursor: "pointer",
                    "&:hover": { color: "rgba(255,255,255,0.85)" }
                  }}
                />
              </Box>
              <Popover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={() => setPopoverAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.text.secondary}`,
                    borderRadius: 2,
                    p: 2,
                    maxWidth: 380,
                    position: "relative"
                  }
                }}>
                <IconButton
                  onClick={() => setPopoverAnchor(null)}
                  sx={styles.closeBtn}
                  size="small">
                  <Close sx={{ height: 14, width: 14 }} />
                </IconButton>
                <Box className="d-flex flex-column" sx={{ marginBottom: 2 }}>
                  <Typography variant="body1Medium">Section Type Guide</Typography>
                  <Typography variant="caption4" color="text.secondary">
                    Once selected, the section type cannot be changed.
                  </Typography>
                </Box>
                {sectionTypeGuide?.map((item) => (
                  <Box key={item.key}>
                    <Box className="d-flex align-items-start gap-1">
                      <Box sx={{ flex: 1 }}>
                        <Box className="d-flex align-items-center gap-1" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                          <Box
                            className="d-flex align-items-center justify-content-center"
                            sx={{
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 0.75,
                              bgcolor:
                                item.tag === "System"
                                  ? "rgba(255,165,0,0.15)"
                                  : "rgba(100,200,100,0.15)",
                              lineHeight: 1
                            }}>
                            <Typography
                              variant="emphasisText"
                              color={
                                item?.tag === "System"
                                  ? theme.palette.orange.secondary
                                  : theme.palette.green.main
                              }>
                              {item?.tag}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                          {item?.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Popover>

              <AppSelect
                name="type"
                value={formik.values.type}
                isRequired={true}
                onChange={formik.handleChange}
                menuItems={
                  !isEdit
                    ? sectionTypes?.filter((item) =>
                        ["CUSTOM", "NOTES", "TEXT"].includes(item.value)
                      )
                    : sectionTypes
                }
                disabled={isEdit}
                error={formik.touched.type && Boolean(formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
              />
              {sectionTypeGuide?.find((item) => item.key === formik.values.type) && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: theme.palette.primary.hover
                  }}>
                  <Typography variant="body2" color="rgba(255,255,255,0.6)">
                    {sectionTypeGuide?.find((item) => item.key === formik.values.type)?.description}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} className="d-flex justify-content-center gap-3 mt-2">
              <AppButton onClick={onClose} variant="outlined" sx={{ height: 48, width: 150 }}>
                Cancel
              </AppButton>
              <AppButton onClick={formik.handleSubmit} sx={{ height: 48, width: 150 }}>
                {isEdit ? "Update" : "Save"}
              </AppButton>
            </Grid>
          </Grid>
        </CommonModal>
      )}
    </Formik>
  )
}

export default SectionModal
